import toast from "react-hot-toast";
import VideoCard from "../../common/VideoCard";
import { FaDeleteLeft } from "react-icons/fa6";
import { IoMdArrowBack } from "react-icons/io";
import { FiEye, FiEyeOff } from "react-icons/fi";
import { RiCloseCircleFill } from "react-icons/ri";
import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { motion, AnimatePresence } from "framer-motion";
import { useDispatch, useSelector } from "react-redux";
import { HiOutlineDotsVertical } from "react-icons/hi";
import useSelectVideo from "../../../hooks/useSelectVideo";
import { useLocation, useNavigate } from "react-router-dom";
import { getPrivateVideos, removePrivateVideoFromList } from "../../../redux/ApiSlice/AccountProfileSlice";
import {
    deletePrivatePin,
    getPrivateHas,
    getVerifyPin,
    sendOtpForgotPin,
    deletePrivatePost,
    makePrivatePostPublic,
} from "../../../redux/ApiSlice/AccountPrivateSlice";
import { Loader2 } from "lucide-react";

function PrivateTab() {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const selectVideo = useSelectVideo();
    const location = useLocation();

    const ProfileDetails = useSelector((state) => state.AccountProfile);
    const {
        User_profile,
        private_Videos,
        privateVideosNextPage,
        privateVideosHasMore,
        privateVideosLoadingMore,
        loading,
    } = ProfileDetails;

    const PrivateDetails = useSelector((state) => state.AccountPrivate);
    const { hasPin, deletePrivatePostLoading, makePrivatePublicLoading } = PrivateDetails;

    const [inputValue, setInputValue] = useState("");
    const [showPrivate, setShowPrivate] = useState(sessionStorage.getItem("privateVerified") === "true");
    const [showPassword, setShowPassword] = useState(false);
    const [showPopup, setShowPopup] = useState(false);
    const [showDelete, setShowDelete] = useState(false);
    const [passwordError, setPasswordError] = useState("");

    const menuRef = useRef(null);
    const videoMenuRef = useRef(null);
    const lastVideoRef = useRef(null);
    const observerRef = useRef(null);

    // Per-video action menu (page-level bottom-sheet)
    const [activeMenuId, setActiveMenuId] = useState(null);
    const [portalRoot, setPortalRoot] = useState(null);

    useEffect(() => {
        setPortalRoot(document.getElementById('mobile-layout-root'));
    }, []);

    // Action confirmation modal with PIN
    const [actionModal, setActionModal] = useState(null); // { type: 'delete'|'makePublic', postId }
    const [actionPin, setActionPin] = useState("");
    const [actionPinError, setActionPinError] = useState("");
    const [actionPinShow, setActionPinShow] = useState(false);
    const actionConfirmLoading = deletePrivatePostLoading || makePrivatePublicLoading;

    const handleButtonClick = (value) => {
        if (inputValue.length < 6) setInputValue(inputValue + value);
    };

    // Always-current refs so the keydown closure never goes stale
    const handleSubmitRef = useRef();
    const handleConfirmActionRef = useRef();

    // Keyboard support for both PIN screens
    useEffect(() => {
        const onKeyDown = (e) => {
            if (actionModal) {
                if (/^[0-9]$/.test(e.key)) {
                    setActionPin((prev) => prev.length < 6 ? prev + e.key : prev);
                } else if (e.key === "Backspace") {
                    setActionPin((prev) => prev.slice(0, -1));
                } else if (e.key === "Enter") {
                    handleConfirmActionRef.current?.();
                }
            } else if (!showPrivate) {
                if (/^[0-9]$/.test(e.key)) {
                    setInputValue((prev) => prev.length < 6 ? prev + e.key : prev);
                } else if (e.key === "Backspace") {
                    setInputValue((prev) => prev.slice(0, -1));
                } else if (e.key === "Enter") {
                    handleSubmitRef.current?.();
                }
            }
        };
        window.addEventListener("keydown", onKeyDown);
        return () => window.removeEventListener("keydown", onKeyDown);
    }, [showPrivate, actionModal]); // eslint-disable-line react-hooks/exhaustive-deps

    const handleSubmit = async () => {
        if (inputValue.length < 6) {
            setPasswordError("Password must be 6 digits");
            return;
        }
        try {
            const result = await dispatch(getVerifyPin({ pin: inputValue, userId: User_profile.id }));
            if (result?.payload?.detail === "PIN required for accessing private posts") {
                toast.error("PIN required for accessing private posts");
            } else {
                toast.success("Password is correct");
                sessionStorage.setItem("privateVerified", "true");
                await dispatch(getPrivateVideos({ id: User_profile.id }));
                setShowPrivate(true);
                setInputValue("");
                setPasswordError("");
            }
        } catch {
            toast.error("An error occurred while submitting the password.");
        }
    };

    const HandleSendOtp = async () => {
        if (User_profile?.email) {
            const result = await dispatch(sendOtpForgotPin());
            if (result.payload?.message === "OTP sent to your email.") {
                toast.success(result.payload.message);
                setTimeout(() => navigate("/private/enter-otp"), 2000);
            }
        } else {
            toast.error("Please add your email to your profile to reset your password.");
        }
    };

    const handleDelete = async () => {
        try {
            const result = await dispatch(deletePrivatePin(User_profile.id));
            if (result.payload?.message === "PIN deleted.") {
                sessionStorage.removeItem("privateVerified");
                dispatch(getPrivateHas());
                toast.success("Private videos deleted successfully.");
                setShowDelete(false);
                setShowPrivate(false);
                navigate("/private/empty");
            }
        } catch (error) {
            toast.error(error.error || "Failed to delete private videos");
        }
    };

    const handleSelectVideo = (id) => {
        selectVideo(id, private_Videos, location.pathname);
    };

    // Load videos on mount if already verified via session
    useEffect(() => {
        if (sessionStorage.getItem("privateVerified") === "true" && User_profile?.id) {
            dispatch(getPrivateVideos({ id: User_profile.id }));
            setShowPrivate(true);
        }
    }, [User_profile?.id]); // eslint-disable-line react-hooks/exhaustive-deps

    // Close menus on outside click
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (menuRef.current && !menuRef.current.contains(event.target)) setShowPopup(false);
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    // Infinite scroll via IntersectionObserver on last tile
    useEffect(() => {
        if (!privateVideosHasMore || privateVideosLoadingMore) return;
        if (observerRef.current) observerRef.current.disconnect();

        observerRef.current = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting && privateVideosHasMore && !privateVideosLoadingMore) {
                    dispatch(getPrivateVideos({
                        id: User_profile?.id,
                        nextUrl: privateVideosNextPage,
                        isLoadMore: true,
                    }));
                }
            },
            { threshold: 0.1 }
        );

        if (lastVideoRef.current) observerRef.current.observe(lastVideoRef.current);
        return () => observerRef.current?.disconnect();
    }, [privateVideosHasMore, privateVideosNextPage, privateVideosLoadingMore, private_Videos]); // eslint-disable-line react-hooks/exhaustive-deps

    // Action modal helpers
    const openActionModal = (type, postId) => {
        setActionPin("");
        setActionPinError("");
        setActionPinShow(false);
        setActionModal({ type, postId });
        setActiveMenuId(null);
    };

    const closeActionModal = () => {
        if (actionConfirmLoading) return;
        setActionModal(null);
        setActionPin("");
        setActionPinError("");
        setActionPinShow(false);
    };

    const handleActionPinButton = (value) => {
        if (actionPin.length < 6) setActionPin(actionPin + value);
    };

    const handleConfirmAction = async () => {
        if (actionPin.length < 6) {
            setActionPinError("Enter your 6-digit PIN");
            return;
        }
        try {
            if (actionModal.type === "delete") {
                await dispatch(deletePrivatePost({ postId: actionModal.postId, pin: actionPin })).unwrap();
                dispatch(removePrivateVideoFromList(actionModal.postId));
                toast.success("Post moved to Recently Deleted.");
                closeActionModal();
            } else {
                await dispatch(makePrivatePostPublic({ postId: actionModal.postId, pin: actionPin })).unwrap();
                dispatch(removePrivateVideoFromList(actionModal.postId));
                toast.success("Post is now public.");
                closeActionModal();
            }
        } catch (error) {
            setActionPinError(error?.detail || error?.error || "Failed. Please check your PIN.");
        }
    };

    // Keep refs in sync with latest function on every render
    handleSubmitRef.current = handleSubmit;
    handleConfirmActionRef.current = handleConfirmAction;

    return (
        <>
            {/* Header */}
            <div className="sticky top-0 z-10">
                <div className="relative bg-[#BDE6FF] rounded-b-xl h-12 flex items-center justify-center">
                    <IoMdArrowBack
                        onClick={() => {
                            sessionStorage.removeItem("privateVerified");
                            navigate("/account/settings");
                        }}
                        className="absolute left-2 top-1/2 transform -translate-y-1/2 text-[22px] text-black dark:text-black cursor-pointer"
                    />
                    <p className="font-bold text-[16px] text-black dark:text-black">Private Videos</p>
                </div>
            </div>

            <div className="h-full flex flex-col bg-white dark:bg-[#1E1D21]">
                {!showPrivate && (
                    <div className="flex flex-col items-center justify-center mt-4 px-4">
                        <p className="mb-2 text-center text-sm text-gray-600 dark:text-gray-400">
                            Enter your 6-digit PIN to access
                        </p>

                        {/* PIN Input Display */}
                        <div className="mb-0 relative">
                            <div className="flex gap-2 justify-center mb-2">
                                {[...Array(6)].map((_, index) => (
                                    <div
                                        key={index}
                                        className={`w-10 h-10 rounded-lg flex items-center justify-center border-2 transition-all ${inputValue.length > index
                                            ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/30 dark:border-blue-400'
                                            : 'border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800'
                                            }`}
                                    >
                                        <span className="text-[14px] font-bold text-gray-800 dark:text-white">
                                            {inputValue[index] ? (showPassword ? inputValue[index] : '•') : ''}
                                        </span>
                                    </div>
                                ))}
                            </div>

                            <div className="flex justify-center">
                                <button
                                    onClick={() => setShowPassword(!showPassword)}
                                    className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 transition-colors"
                                >
                                    {showPassword ? <FiEye size={18} /> : <FiEyeOff size={18} />}
                                    <span>{showPassword ? 'Hide PIN' : 'Show PIN'}</span>
                                </button>
                            </div>

                            {passwordError && (
                                <div className="absolute -bottom-8 left-1/2 transform -translate-x-1/2 whitespace-nowrap">
                                    <p className="text-red-500 text-sm font-medium bg-red-50 dark:bg-red-900/30 px-4 py-1 rounded-full">
                                        {passwordError}
                                    </p>
                                </div>
                            )}
                        </div>

                        {/* Numpad */}
                        <div className="w-full max-w-xs mt-10">
                            <div className="flex flex-col gap-2">
                                {[[1, 2, 3], [4, 5, 6], [7, 8, 9]].map((row) => (
                                    <div key={row[0]} className="flex justify-center gap-4">
                                        {row.map((num) => (
                                            <button
                                                key={num}
                                                onClick={() => handleButtonClick(num)}
                                                className="w-14 h-14 rounded-xl bg-white dark:bg-gray-800 shadow-lg hover:shadow-xl active:scale-95 transition-all flex items-center justify-center text-xl font-bold text-gray-800 dark:text-white border border-gray-200 dark:border-gray-700 hover:border-blue-400 dark:hover:border-blue-500"
                                            >
                                                {num}
                                            </button>
                                        ))}
                                    </div>
                                ))}
                                <div className="flex justify-center gap-4">
                                    <button
                                        onClick={() => setShowPopup(!showPopup)}
                                        className="w-14 h-14 rounded-xl bg-gradient-to-br from-gray-100 to-gray-200 dark:from-gray-700 dark:to-gray-800 shadow-lg hover:shadow-xl active:scale-95 transition-all flex items-center justify-center border border-gray-200 dark:border-gray-700"
                                    >
                                        {!showPopup
                                            ? <HiOutlineDotsVertical className="text-xl text-gray-700 dark:text-gray-300" />
                                            : <RiCloseCircleFill className="text-xl text-red-600 dark:text-red-400" />
                                        }
                                    </button>
                                    <button
                                        onClick={() => handleButtonClick(0)}
                                        className="w-14 h-14 rounded-xl bg-white dark:bg-gray-800 shadow-lg hover:shadow-xl active:scale-95 transition-all flex items-center justify-center text-xl font-bold text-gray-800 dark:text-white border border-gray-200 dark:border-gray-700 hover:border-blue-400 dark:hover:border-blue-500"
                                    >
                                        0
                                    </button>
                                    <button
                                        onClick={() => setInputValue(inputValue.slice(0, -1))}
                                        className="w-14 h-14 rounded-xl bg-gradient-to-br from-red-500 to-red-600 shadow-lg hover:shadow-xl active:scale-95 transition-all flex items-center justify-center"
                                    >
                                        <FaDeleteLeft className="text-xl text-white" />
                                    </button>
                                </div>
                            </div>
                        </div>

                        <button
                            onClick={handleSubmit}
                            className="mt-8 w-full max-w-xs h-10 rounded-xl bg-[#BDE6FF] flex items-center justify-center gap-2 text-black border border-black font-bold text-[14px]"
                        >
                            Submit
                        </button>

                        {/* Options popup */}
                        {showPopup && (
                            <div
                                ref={menuRef}
                                className="fixed top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 bg-white dark:bg-gray-800 rounded-2xl shadow-2xl p-4 z-50 w-64 border border-gray-200 dark:border-gray-700"
                            >
                                <h3 className="text-lg font-bold text-gray-800 dark:text-white mb-3 pb-2 border-b border-gray-200 dark:border-gray-700">
                                    Options
                                </h3>
                                <div className="space-y-2">
                                    <button
                                        onClick={HandleSendOtp}
                                        className="w-full text-left px-4 py-3 rounded-lg hover:bg-blue-50 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300 font-medium transition-colors flex items-center gap-3"
                                    >
                                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
                                        </svg>
                                        Forgot Password
                                    </button>
                                    <button
                                        onClick={() => navigate("/private/resetpassword")}
                                        className="w-full text-left px-4 py-3 rounded-lg hover:bg-blue-50 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300 font-medium transition-colors flex items-center gap-3"
                                    >
                                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                                        </svg>
                                        Reset Password
                                    </button>
                                    <button
                                        onClick={() => setShowDelete(true)}
                                        className="w-full text-left px-4 py-3 rounded-lg hover:bg-red-50 dark:hover:bg-red-900/30 text-red-600 dark:text-red-400 font-medium transition-colors flex items-center gap-3"
                                    >
                                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                        </svg>
                                        Delete Private
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>
                )}

                {/* Private Videos Section */}
                {showPrivate && (
                    <div className="flex-1 flex flex-col px-4 py-4 bg-white dark:bg-black">
                        {/* Videos Grid */}
                        {!loading && private_Videos?.length > 0 && (
                            <>
                                <div className="grid grid-cols-3 gap-1.5 overflow-auto no-scrollbar pb-4">
                                    {private_Videos.map((item, index) => {
                                        const isLast = index === private_Videos.length - 1;
                                        return (
                                            <div
                                                key={item.id}
                                                ref={isLast ? lastVideoRef : null}
                                                className="relative"
                                            >
                                                <VideoCard
                                                    belowDecs={true}
                                                    threeDots={false}
                                                    id={item.id}
                                                    video={item.video_url}
                                                    thumbnail={item.thumbnail_url}
                                                    likes_count={item.likes_count}
                                                    view_count={item.view_count}
                                                    authorId={item.created_by?.id}
                                                    avatar_url={item.created_by?.avatar_url}
                                                    body={item.post_description}
                                                    selectVideo={handleSelectVideo}
                                                />
                                                {/* Per-video action trigger */}
                                                <button
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        setActiveMenuId(activeMenuId === item.id ? null : item.id);
                                                    }}
                                                    className="absolute top-1.5 right-1.5 bg-black/50 hover:bg-black/70 rounded-full p-1 z-10 transition-colors"
                                                >
                                                    <HiOutlineDotsVertical className="text-white text-sm" />
                                                </button>
                                            </div>
                                        );
                                    })}
                                </div>

                                {/* Load more spinner */}
                                {privateVideosLoadingMore && (
                                    <div className="flex justify-center py-4">
                                        <Loader2 size={24} className="animate-spin text-gray-400 dark:text-gray-500" />
                                    </div>
                                )}
                            </>
                        )}

                        {/* Empty State */}
                        {!loading && private_Videos?.length === 0 && (
                            <div className="flex-1 flex flex-col justify-center items-center">
                                <div className="w-32 h-32 bg-gray-200 dark:bg-gray-800 rounded-full flex items-center justify-center mb-6">
                                    <svg className="w-16 h-16 text-gray-400 dark:text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                                    </svg>
                                </div>
                                <h3 className="text-xl font-bold text-gray-800 dark:text-white mb-2">No Private Videos</h3>
                            </div>
                        )}

                        {/* Loading State */}
                        {loading && (
                            <div className="flex-1 flex flex-col justify-center items-center">
                                <div className="relative w-16 h-16 mb-4">
                                    <div className="absolute inset-0 border-4 border-blue-200 dark:border-blue-900 rounded-full"></div>
                                    <div className="absolute inset-0 border-4 border-t-blue-600 dark:border-t-blue-400 rounded-full animate-spin"></div>
                                </div>
                                <p className="text-gray-600 dark:text-gray-400 font-medium">Loading your videos...</p>
                            </div>
                        )}
                    </div>
                )}
            </div>

            {/* Delete PIN Confirmation Modal */}
            {showDelete && portalRoot && createPortal(
                <div className="absolute inset-0 flex items-center justify-center bg-black/60 backdrop-blur-sm z-[9999] px-4">
                    <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl p-6 max-w-sm w-full">
                        <div className="w-16 h-16 bg-red-100 dark:bg-red-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
                            <svg className="w-8 h-8 text-red-600 dark:text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                            </svg>
                        </div>
                        <h3 className="text-xl font-bold text-gray-900 dark:text-white text-center mb-2">
                            Delete Private Password?
                        </h3>
                        <p className="text-sm text-gray-600 dark:text-gray-400 text-center mb-6">
                            Are you sure you want to delete the private password? This action cannot be undone and all your private videos will be removed.
                        </p>
                        <div className="flex gap-3">
                            <button
                                onClick={() => setShowDelete(false)}
                                className="flex-1 py-3 px-4 bg-gray-200 hover:bg-gray-300 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-800 dark:text-white rounded-xl font-semibold transition-colors"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleDelete}
                                className="flex-1 py-3 px-4 bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 text-white rounded-xl font-semibold transition-all shadow-lg hover:shadow-xl"
                            >
                                Delete
                            </button>
                        </div>
                    </div>
                </div>,
                portalRoot
            )}

            {/* Per-video action drawer */}
            {portalRoot && createPortal(
                <AnimatePresence>
                    {activeMenuId && (
                        <motion.div
                            key="drawer-root"
                            className="absolute inset-0"
                            style={{ zIndex: 9999 }}
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            transition={{ duration: 0.2 }}
                        >
                            {/* Backdrop */}
                            <div
                                className="absolute inset-0 bg-black/50"
                                onClick={() => setActiveMenuId(null)}
                            />
                            {/* Sheet */}
                            <motion.div
                                initial={{ y: "100%" }}
                                animate={{ y: 0 }}
                                exit={{ y: "100%" }}
                                transition={{ type: "spring", damping: 28, stiffness: 300 }}
                                className="absolute bottom-0 left-0 right-0 bg-white dark:bg-gray-900 rounded-t-3xl shadow-2xl"
                                style={{ paddingBottom: "env(safe-area-inset-bottom, 16px)" }}
                            >
                                {/* Handle */}
                                <div className="pt-3 pb-2 flex justify-center">
                                    <div className="w-12 h-1.5 bg-gray-300 dark:bg-gray-600 rounded-full" />
                                </div>

                                <p className="text-center text-xs text-gray-400 dark:text-gray-500 font-medium uppercase tracking-widest mb-3">
                                    Video options
                                </p>

                                <div className="px-4 space-y-2 pb-4">
                                    <button
                                        onClick={() => openActionModal("makePublic", activeMenuId)}
                                        className="w-full flex items-center gap-4 px-4 py-4 rounded-2xl text-[15px] font-medium text-gray-800 dark:text-gray-100 bg-gray-50 dark:bg-gray-800 active:bg-blue-50 dark:active:bg-gray-700 transition-colors"
                                    >
                                        <span className="w-10 h-10 flex items-center justify-center rounded-full bg-blue-100 dark:bg-blue-900/50 flex-shrink-0">
                                            <svg className="w-5 h-5 text-blue-600 dark:text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                            </svg>
                                        </span>
                                        <div className="text-left">
                                            <p className="font-semibold">Make Public</p>
                                            <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">Move this video to your public profile</p>
                                        </div>
                                    </button>

                                    <button
                                        onClick={() => openActionModal("delete", activeMenuId)}
                                        className="w-full flex items-center gap-4 px-4 py-4 rounded-2xl text-[15px] font-medium text-red-600 dark:text-red-400 bg-gray-50 dark:bg-gray-800 active:bg-red-50 dark:active:bg-red-900/20 transition-colors"
                                    >
                                        <span className="w-10 h-10 flex items-center justify-center rounded-full bg-red-100 dark:bg-red-900/50 flex-shrink-0">
                                            <svg className="w-5 h-5 text-red-600 dark:text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                            </svg>
                                        </span>
                                        <div className="text-left">
                                            <p className="font-semibold">Delete</p>
                                            <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">Move to Recently Deleted</p>
                                        </div>
                                    </button>

                                    <button
                                        onClick={() => setActiveMenuId(null)}
                                        className="w-full py-4 rounded-2xl text-[15px] font-semibold text-gray-500 dark:text-gray-400 bg-gray-100 dark:bg-gray-700/50 transition-colors mt-1"
                                    >
                                        Cancel
                                    </button>
                                </div>
                            </motion.div>
                        </motion.div>
                    )}
                </AnimatePresence>,
                portalRoot
            )}

            {/* Action Confirmation Modal (Delete / Make Public) with PIN */}
            {actionModal && portalRoot && createPortal(
                <div className="absolute inset-0 bg-black/60 backdrop-blur-sm z-[9999] flex items-end justify-center">
                    <div className="bg-white dark:bg-gray-900 rounded-t-3xl p-6 w-full max-w-md">
                        <h3 className="font-bold text-lg text-center text-gray-900 dark:text-white mb-1">
                            {actionModal.type === "delete" ? "Move to Recently Deleted?" : "Make Post Public?"}
                        </h3>
                        <p className="text-sm text-center text-gray-500 dark:text-gray-400 mb-5">
                            Enter your PIN to confirm
                        </p>

                        {/* PIN display */}
                        <div className="flex gap-2 justify-center mb-1">
                            {[...Array(6)].map((_, i) => (
                                <div
                                    key={i}
                                    className={`w-10 h-10 rounded-lg border-2 flex items-center justify-center transition-all ${actionPin.length > i
                                        ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/30 dark:border-blue-400'
                                        : 'border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800'
                                        }`}
                                >
                                    <span className="text-sm font-bold text-gray-800 dark:text-white">
                                        {actionPin[i] ? (actionPinShow ? actionPin[i] : '•') : ''}
                                    </span>
                                </div>
                            ))}
                        </div>

                        <div className="flex justify-center mb-3">
                            <button
                                onClick={() => setActionPinShow(!actionPinShow)}
                                className="flex items-center gap-1.5 text-xs text-gray-500 dark:text-gray-400 hover:text-blue-500"
                            >
                                {actionPinShow ? <FiEye size={14} /> : <FiEyeOff size={14} />}
                                {actionPinShow ? 'Hide' : 'Show'}
                            </button>
                        </div>

                        {actionPinError && (
                            <p className="text-red-500 text-xs text-center mb-3 font-medium">{actionPinError}</p>
                        )}

                        {/* Numpad */}
                        <div className="flex flex-col gap-2">
                            {[[1, 2, 3], [4, 5, 6], [7, 8, 9]].map((row) => (
                                <div key={row[0]} className="flex justify-center gap-4">
                                    {row.map((num) => (
                                        <button
                                            key={num}
                                            onClick={() => handleActionPinButton(String(num))}
                                            className="w-14 h-14 rounded-xl bg-white dark:bg-gray-800 shadow-md active:scale-95 transition-all flex items-center justify-center text-xl font-bold text-gray-800 dark:text-white border border-gray-200 dark:border-gray-700"
                                        >
                                            {num}
                                        </button>
                                    ))}
                                </div>
                            ))}
                            <div className="flex justify-center gap-4">
                                <button
                                    onClick={closeActionModal}
                                    className="w-14 h-14 rounded-xl bg-gray-100 dark:bg-gray-700 shadow-md active:scale-95 transition-all flex items-center justify-center text-base font-bold text-gray-700 dark:text-gray-300"
                                >
                                    ✕
                                </button>
                                <button
                                    onClick={() => handleActionPinButton("0")}
                                    className="w-14 h-14 rounded-xl bg-white dark:bg-gray-800 shadow-md active:scale-95 transition-all flex items-center justify-center text-xl font-bold text-gray-800 dark:text-white border border-gray-200 dark:border-gray-700"
                                >
                                    0
                                </button>
                                <button
                                    onClick={() => setActionPin(actionPin.slice(0, -1))}
                                    className="w-14 h-14 rounded-xl bg-gradient-to-br from-red-500 to-red-600 shadow-md active:scale-95 transition-all flex items-center justify-center"
                                >
                                    <FaDeleteLeft className="text-xl text-white" />
                                </button>
                            </div>
                        </div>

                        <button
                            onClick={handleConfirmAction}
                            disabled={actionConfirmLoading}
                            className={`mt-4 w-full h-11 rounded-xl flex items-center justify-center gap-2 font-bold text-sm transition-all ${actionModal.type === "delete"
                                ? "bg-red-600 hover:bg-red-700 text-white"
                                : "bg-[#BDE6FF] border border-black text-black"
                                } ${actionConfirmLoading ? "opacity-70 cursor-not-allowed" : ""}`}
                        >
                            {actionConfirmLoading
                                ? <><Loader2 size={16} className="animate-spin" /> Processing...</>
                                : actionModal.type === "delete" ? "Move to Recently Deleted" : "Make Public"
                            }
                        </button>
                    </div>
                </div>,
                portalRoot
            )}
        </>
    );
}

export default PrivateTab;
